import { useState } from "react";
import styles from "./registration-modal.module.css";
import { useTheme } from "@/components/context/ThemeContext";
import PaymentOptions from "@/components/checkout/PaymentOptions";

export default function RegistrationModal({ tournament, onClose, onRegister }) {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    paymentMethod: "",
    gameId: "",
    teamName: "",
    playerRank: "",
    additionalInfo: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (tournament.registrationPrice > 0 && !formData.paymentMethod.trim()) {
      newErrors.paymentMethod = "Payment method is required";
    }
    if (!formData.gameId.trim()) {
      newErrors.gameId = "Game ID is required";
    }
    if (formData.teamName.trim().length > 50) {
      newErrors.teamName = "Team name must be 50 characters or less";
    }
    if (formData.additionalInfo.trim().length > 500) {
      newErrors.additionalInfo =
        "Additional info must be 500 characters or less";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onRegister(formData);

      onClose();
    } catch (error) {
      console.error("Registration error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`${styles.modalOverlay} ${theme === "dark" ? "dark" : ""}`}
      onClick={onClose}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.title}>Register for Tournament</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.tournamentBanner}>
          <span className={styles.gameBadge}>{tournament.game}</span>
          <h3 className={styles.tournamentName}>{tournament.name}</h3>
          <p className={styles.tournamentFee}>
            Entry Fee:{" "}
            {tournament.registrationPrice > 0
              ? `₹${tournament.registrationPrice}`
              : "Free"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>


          {tournament.registrationPrice > 0 && (
            <div className={styles.formGroup}>
              <PaymentOptions
                selectedMethod={formData.paymentMethod}
                onPaymentMethodChange={(method) =>
                  setFormData((prev) => ({ ...prev, paymentMethod: method }))
                }
              />
              {errors.paymentMethod && (
                <span className={styles.errorMessage}>
                  {errors.paymentMethod}
                </span>
              )}
            </div>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="gameId">Game ID / Username *</label>
            <input
              type="text"
              id="gameId"
              name="gameId"
              value={formData.gameId}
              onChange={handleChange}
              placeholder="e.g., Player#1234"
              className={errors.gameId ? styles.inputError : ""}
            />
            {errors.gameId && (
              <span className={styles.errorMessage}>{errors.gameId}</span>
            )}
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="teamName">Team Name</label>
              <input
                type="text"
                id="teamName"
                name="teamName"
                value={formData.teamName}
                onChange={handleChange}
                placeholder="e.g., Phoenix Legends"
                maxLength="50"
              />
              <span className={styles.charCounter}>
                {formData.teamName.length}/50
              </span>
              {errors.teamName && (
                <span className={styles.errorMessage}>{errors.teamName}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="playerRank">Player Rank</label>
              <input
                type="text"
                id="playerRank"
                name="playerRank"
                value={formData.playerRank}
                onChange={handleChange}
                placeholder="e.g., Diamond, Gold"
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="additionalInfo">Additional Information</label>
            <textarea
              id="additionalInfo"
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleChange}
              placeholder="Any additional details about your registration..."
              maxLength="500"
              rows="4"
              className={styles.textarea}
            />
            <span className={styles.charCounter}>
              {formData.additionalInfo.length}/500
            </span>
            {errors.additionalInfo && (
              <span className={styles.errorMessage}>
                {errors.additionalInfo}
              </span>
            )}
          </div>

          <div className={styles.formButtons}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Registering..." : "Complete Registration"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
